import { Button } from '@mui/material';
import React from 'react';

type Props = {
	/** When this changes (e.g. the route pathname), a crashed subtree remounts. */
	resetKey?: unknown;
	children?: React.ReactNode;
};

type State = { error: Error | null };

/**
 * Route-level error boundary: a render crash inside a page shows a recoverable
 * fallback instead of a full white screen, and navigating away resets it.
 */
class RouteErrorBoundary extends React.Component<Props, State> {
	state: State = { error: null };

	static getDerivedStateFromError(error: Error): State {
		return { error };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		console.error('[RouteErrorBoundary]', error, info.componentStack);
	}

	componentDidUpdate(prevProps: Props) {
		if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
			this.setState({ error: null });
		}
	}

	render() {
		if (this.state.error) {
			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '1rem',
						minHeight: '60vh',
						padding: '2rem',
						textAlign: 'center',
					}}
				>
					<h2>Something went wrong</h2>
					<p
						style={{
							color: '#666',
							maxWidth: '32rem',
							wordBreak: 'break-word',
						}}
					>
						{this.state.error.message}
					</p>
					<Button variant="contained" onClick={() => window.location.reload()}>
						Reload
					</Button>
				</div>
			);
		}
		return this.props.children ?? null;
	}
}

export default RouteErrorBoundary;
